import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Visibility, Search, MoreVert, Block } from '@mui/icons-material';
import { useCustomers } from '../hooks';
import { colors } from '../theme/theme';
import { customerService } from '../services';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';

export const CustomersPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const { customers, isLoading, error } = useCustomers({
    page: page + 1,
    per_page: rowsPerPage,
    search,
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCustomer(null);
  };

  const handleViewDetails = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!selectedCustomer) {
      return;
    }

    const customerId = selectedCustomer.id;
    handleMenuClose();

    // Navigate after a small delay to ensure menu closes properly
    setTimeout(() => {
      navigate(`/customers/${customerId}`);
    }, 0);
  };

  const handleSuspend = async () => {
    if (!selectedCustomer) return;

    const action = selectedCustomer.status === 'suspended' ? 'activate' : 'suspend';
    const confirmMessage = `Are you sure you want to ${action} this customer?`;

    if (!window.confirm(confirmMessage)) {
      handleMenuClose();
      return;
    }

    try {
      await customerService.toggleSuspend(selectedCustomer.id);
      showSnackbar(`Customer ${action}d successfully`, 'success');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      handleMenuClose();
    } catch (error) {
      showSnackbar(`Failed to ${action} customer`, 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => `${row.first_name || ''} ${row.last_name || ''}`,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
      valueGetter: (value) => value || 'N/A',
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => value || 'N/A',
    },
    {
      field: 'id_number',
      headerName: 'ID Number',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => value || 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Customers"
          subtitle="Manage all customers - individuals and businesses"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Customers' },
          ]}
          action={
            <Button variant="contained" startIcon={<Add />}>
              Add Customer
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load customers. Please try again.
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            transition: 'box-shadow 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={customers?.data || []}
                columns={columns}
                loading={isLoading}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationModel={{ page, pageSize: rowsPerPage }}
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setRowsPerPage(model.pageSize);
                }}
                rowCount={customers?.meta?.total || 0}
                paginationMode="server"
                disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `1px solid ${colors.grey[200]}`,
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: colors.grey[50],
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `1px solid ${colors.grey[200]}`,
                    },
                  }}
                />
              </Box>
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={(e) => handleViewDetails(e)}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleSuspend} sx={{ color: colors.warning.main }}>
            <ListItemIcon>
              <Block fontSize="small" sx={{ color: colors.warning.main }} />
            </ListItemIcon>
            <ListItemText>
              {selectedCustomer?.status === 'suspended' ? 'Activate' : 'Suspend'}
            </ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </AdminLayout>
  );
};
